/**
 * components/BetaApplication/steps/AccountInformationStep.tsx
 * Account Information Step - Step 1 of Beta Application
 *
 * Collects basic account information:
 * - Email address
 * - Full name
 * - Discord username
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';

// 2. Internal absolute imports
import { Form, Input } from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';
import { useBetaApplicationData } from '../hooks/useBetaApplicationData';
import {
  validateAccountStep
} from '../utils/betaApplicationValidation';

/**
 * Account Information Step Component
 *
 * Complete functional form for collecting basic account information
 * with real-time validation and WizardDataProvider integration.
 */
export const AccountInformationStep: React.FC = () => {
  const { data: draft, updateData } = useBetaApplicationData();

  // Field touched states for validation timing
  const [emailTouched, setEmailTouched] = useState(false);
  const [fullNameTouched, setFullNameTouched] = useState(false);
  const [discordUsernameTouched, setDiscordUsernameTouched] = useState(false);

  // Autofill detection and re-render management
  const [renderKey, setRenderKey] = useState(0);
  const autofillTimeoutRef = useRef<number | null>(null);
  const lastValuesRef = useRef({ email: '', fullName: '' });

  // Debounced autofill completion detection
  const triggerAutofillFix = useCallback(() => {
    // Clear any existing timeout
    if (autofillTimeoutRef.current) {
      window.clearTimeout(autofillTimeoutRef.current);
    }

    // Set a new timeout to trigger re-render after autofill is likely complete
    autofillTimeoutRef.current = window.setTimeout(() => {
      setRenderKey(prev => prev + 1);
    }, 150); // 150ms delay to allow autofill to complete multiple fields
  }, []);

  // Initialize lastValuesRef with current draft values
  useEffect(() => {
    if (draft) {
      lastValuesRef.current = {
        email: draft.account.email,
        fullName: draft.account.fullName
      };
    }
  }, [draft]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autofillTimeoutRef.current) {
        window.clearTimeout(autofillTimeoutRef.current);
      }
    };
  }, []);

  // Local validation functions for HeroUI Input components
  const validateEmailField = (value: string): string | null => {
    if (!value.trim()) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return "Please enter a valid email address";
    return null;
  };

  const validateFullNameField = (value: string): string | null => {
    if (!value.trim()) return "Full name is required";
    if (value.trim().length < 2) return "Please enter your full name";
    if (value.length > 100) return "Name is too long";
    return null;
  };

  const validateDiscordUsernameField = (value: string): string | null => {
    if (!value.trim()) return "Discord username is required";
    const discordRegex = /^[a-zA-Z0-9_.]{2,32}$/;
    if (!discordRegex.test(value)) {
      return "Please enter a valid Discord username (2-32 characters, letters, numbers, _ and . only)";
    }
    return null;
  };

  // Real-time validation - only show errors after field is touched
  const emailError = useMemo(() => {
    if (!emailTouched || !draft) return null;
    return validateEmailField(draft.account.email);
  }, [emailTouched, draft]);

  const fullNameError = useMemo(() => {
    if (!fullNameTouched || !draft) return null;
    return validateFullNameField(draft.account.fullName);
  }, [fullNameTouched, draft]);

  const discordUsernameError = useMemo(() => {
    if (!discordUsernameTouched || !draft) return null;
    return validateDiscordUsernameField(draft.account.discordUsername);
  }, [discordUsernameTouched, draft]);

  // Update step completion status when validation changes
  useEffect(() => {
    if (!draft) return;

    const isStepValid = validateAccountStep(draft).isValid;
    const isCompleted = draft.completedSteps.includes('account');

    if (isStepValid && !isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: [...current.completedSteps, 'account']
      }));
    } else if (!isStepValid && isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: current.completedSteps.filter(step => step !== 'account')
      }));
    }
  }, [draft, updateData]);

  // Form submission handler (for enter key behavior)
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // Form submission is handled by WizardLayout navigation
  };

  // Field change handlers
  const handleEmailChange = (value: string): void => {
    const oldEmail = lastValuesRef.current.email;
    lastValuesRef.current.email = value;

    // Detect if this might be autofill (rapid value change from empty to filled)
    if (oldEmail === '' && value !== '' && value.includes('@')) {
      triggerAutofillFix();
    }

    updateData((current) => ({
      ...current,
      account: { ...current.account, email: value },
      lastModified: new Date().toISOString()
    }));
  };

  const handleFullNameChange = (value: string): void => {
    const oldFullName = lastValuesRef.current.fullName;
    lastValuesRef.current.fullName = value;

    // Detect if this might be autofill (rapid value change from empty to filled)
    if (oldFullName === '' && value !== '' && value.includes(' ')) {
      triggerAutofillFix();
    }

    updateData((current) => ({
      ...current,
      account: { ...current.account, fullName: value },
      lastModified: new Date().toISOString()
    }));
  };

  const handleDiscordUsernameChange = (value: string): void => {
    updateData((current) => ({
      ...current,
      account: { ...current.account, discordUsername: value },
      lastModified: new Date().toISOString()
    }));
  };

  if (!draft) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center">
          <p className="text-default-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-semibold mb-2">Account Information</h2>
        <p className="text-default-500">
          {`Let's start with your basic account details for the ${Brand.displayName} Beta Program.`}
        </p>
      </div>

      <Form
        validationBehavior="aria"
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        {/* Email Input */}
        <Input
          key={`email-${renderKey}`}
          type="email"
          label="Email Address"
          placeholder="Enter your email address"
          description="We'll use this to communicate about your beta application"
          value={draft.account.email}
          onValueChange={handleEmailChange}
          onBlur={() => setEmailTouched(true)}
          validate={validateEmailField}
          isRequired
          isClearable
          autoComplete="email"
          errorMessage={emailError}
          isInvalid={!!emailError}
          classNames={{
            input: "text-base", // 16px font size to prevent mobile zoom
          }}
        />

        {/* Full Name Input */}
        <Input
          key={`fullName-${renderKey}`}
          type="text"
          label="Full Name"
          placeholder="Enter your full name"
          description="Your full name as you'd like it to appear in the beta program"
          value={draft.account.fullName}
          onValueChange={handleFullNameChange}
          onBlur={() => setFullNameTouched(true)}
          validate={validateFullNameField}
          isRequired
          isClearable
          autoComplete="name"
          errorMessage={fullNameError}
          isInvalid={!!fullNameError}
          classNames={{
            input: "text-base",
          }}
        />

        {/* Discord Username Input */}
        <Input
          type="text"
          label="Discord Username"
          placeholder="Enter your Discord username"
          description="For access to our private beta testing Discord community"
          value={draft.account.discordUsername}
          onValueChange={handleDiscordUsernameChange}
          onBlur={() => setDiscordUsernameTouched(true)}
          validate={validateDiscordUsernameField}
          isRequired
          isClearable
          errorMessage={discordUsernameError}
          isInvalid={!!discordUsernameError}
          classNames={{
            input: "text-base",
          }}
        />
      </Form>
    </div>
  );
};